import { useEffect, useState } from "react";
import { Compass, Target, TrendingUp } from "lucide-react";
import client from "../lib/api";

/**
 * Career match. Reads the cell (fit/readiness or capability/positioning)
 * from the person's own read, plus the archetype list.
 * All data is client-cached from the report; no buyer-facing endpoint
 * is called (SI-03).
 */
export default function CareerMatchScreen({ product }) {
  const [report, setReport] = useState(null);
  useEffect(() => {
    const raw = localStorage.getItem("klareet.report");
    if (raw) {
      try { setReport(JSON.parse(raw)); return; } catch (_e) { /* ignore */ }
    }
    // No cached read (fresh sign in on a new device). Pull the current
    // read so the career surface loads even with empty localStorage.
    client.get(`/demo/read/latest?product=${product}`)
      .then((r) => {
        const fetched = r?.data?.report;
        if (fetched) {
          setReport(fetched);
          try { localStorage.setItem("klareet.report", JSON.stringify(fetched)); } catch (_e) { /* ignore */ }
        }
      })
      .catch(() => { /* no read yet */ });
  }, [product]);

  if (!report) return <EmptyPrompt />;
  const cell = report.cell;
  const primary = report.archetype?.primary;
  const secondary = report.archetype?.secondary;
  const isGrow = product === "grow";
  const fitVal = Number(cell?.x_value ?? 0);
  const readVal = Number(cell?.y_value ?? 0);
  const fitPct = Math.round(Math.max(0, Math.min(5, fitVal)) * 20);
  const readPct = Math.round(Math.max(0, Math.min(5, readVal)) * 20);

  return (
    <div className="max-w-4xl mx-auto px-5 pt-8 pb-24">
      <div className="k-fade-in">
        <div className="k-label mb-2">career match</div>
        <h1 className="klareet-serif mb-3" style={{ fontSize: "clamp(1.8rem, 4vw, 2.4rem)", lineHeight: 1.1 }} data-testid="career-title">
          {isGrow ? "What you're good at, meet where you fit." : "Where you fit best, from your own words."}
        </h1>
        <p className="k-hero-sub mb-8">Two things, both from your own answers. This is not a ranking, and it never becomes one.</p>
      </div>

      <div className="k-card mb-6 k-fade-in" data-testid="cell-card">
        <div className="k-quiet mb-2 flex items-center gap-2"><Target size={14} /> {cell?.quadrant_or_cell}</div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <Bar label={cell?.x_label} value={fitVal} pct={fitPct} tone="blue" testid="cell-x" />
          <Bar label={cell?.y_label} value={readVal} pct={readPct} tone="violet" testid="cell-y" />
        </div>
        <div className="mt-5 pt-4 grid sm:grid-cols-2 gap-4" style={{ borderTop: "1px solid hsl(var(--klareet-line-soft))" }} data-testid="cell-explainer">
          <div>
            <div className="k-label mb-1" style={{ color: "hsl(var(--klareet-blue))" }}>what {cell?.x_label} means</div>
            <p className="text-sm" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
              {isGrow
                ? "How strong your thinking and skills read across the questions you answered."
                : "How clearly one direction pulls ahead of the rest in your own answers, not against anyone else."}
            </p>
          </div>
          <div>
            <div className="k-label mb-1" style={{ color: "hsl(var(--klareet-violet))" }}>what {cell?.y_label} means</div>
            <p className="text-sm" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
              {isGrow
                ? "How set up you are to put those skills to work where people can see them."
                : "How set up you are to move on it right now: follow-through, steadiness, and appetite to grow."}
            </p>
          </div>
        </div>
      </div>

      <div className="k-label mb-3 flex items-center gap-2" data-testid="fits-heading"><Compass size={13} /> your two closest fits</div>
      <div className="grid sm:grid-cols-2 gap-5" data-testid="archetype-list">
        {[primary, secondary].filter(Boolean).map((a) => (
          <div key={a.code} className="k-card k-lift" data-testid={`match-arch-${a.code}`}>
            <div className="k-chip mb-2">{a.code}</div>
            <div className="klareet-serif" style={{ fontSize: "1.35rem" }}>{a.name}</div>
            <div className="k-quiet mt-1">{a.tag}</div>
            <p className="text-sm mt-3" style={{ color: "hsl(var(--klareet-ink-soft))" }}>{a.blurb}</p>
            {a.why_fit && (
              <p className="text-sm mt-3 klareet-serif" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }} data-testid={`why-fit-${a.code}`}>
                {a.why_fit}
              </p>
            )}
            {Array.isArray(a.roles) && a.roles.length > 0 && (
              <div className="mt-4" data-testid={`roles-${a.code}`}>
                <div className="k-label mb-2">example paths</div>
                <div className="flex flex-wrap gap-1.5">
                  {a.roles.map((r, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 rounded-full text-xs"
                      style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))", color: "hsl(var(--klareet-ink))" }}
                      data-testid={`role-${a.code}-${i}`}
                    >
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="klareet-mono !text-xs k-quiet mt-3">fit {Number(a.fit_score || 0).toFixed(2)}</div>
          </div>
        ))}
      </div>
      <p className="k-quiet !text-xs mt-3" data-testid="roles-disclaimer">
        Example paths, not a verdict. They are there to spark ideas, never to box you in.
      </p>

      <div className="k-warm-card p-5 mt-6" data-testid="fairness-note">
        <div className="flex items-start gap-3">
          <TrendingUp size={16} style={{ color: "hsl(var(--klareet-amber))" }} className="mt-1" />
          <div>
            <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>We keep it wide</div>
            <p className="text-sm mt-1" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
              Klareet shows you options and never rules you out for who you are. If a path looks close, you can explore it even when it is not the top number.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bar({ label, value, pct, tone, testid }) {
  const color = tone === "blue" ? "hsl(var(--klareet-blue))" : "hsl(var(--klareet-violet))";
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="k-label" style={{ color }}>{label}</div>
        <div className="klareet-mono text-sm" style={{ color }}>{value?.toFixed?.(2) ?? value}</div>
      </div>
      <div className="k-progress-track mt-2">
        <div className="k-progress-bar" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function EmptyPrompt() {
  return (
    <div className="max-w-2xl mx-auto px-5 pt-16 pb-24 text-center">
      <Compass size={40} className="mx-auto mb-3" style={{ color: "hsl(var(--klareet-muted))" }} />
      <h2 className="klareet-serif mb-2" style={{ fontSize: "1.5rem" }}>No read yet</h2>
      <p className="k-quiet">Finish a conversation with Klair to see your career match.</p>
    </div>
  );
}
